import React, { useCallback, useEffect, useState } from 'react';
import '../../../css/eyeButton.css';
import Select from 'react-select';
import { Modal, Spinner } from 'react-bootstrap';
import SearchableUserTypeDropdown from '../permission/SearchableUserTypeDropdown';
import axiosInstance from '../../../services/AxiosInstance';
import PageTitle from '../../../jsx/layouts/PageTitle';
import notify from '../../../utils/notification';

// only a level-3 (super) admin can approve an accreditation
const isApprover = () => {
  try {
    return JSON.parse(localStorage.getItem('login_Details'))?.user?.user_type === 3;
  } catch {
    return false;
  }
};

const EMPTY = { first_name: '', middle_name: '', last_name: '', email: '', phone: '', gender: '', password: '', venue_id: null, user_type_id: null, zone_ids: [], facility_ids: [], transport_ids: [], dining_ids: [] };
const NO_OPTIONS = { venues: [], zones: [], dining: [], transport: [], facilities: [], user_types: [] };
const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'OTHERS', label: 'Other' },
];
const ID_FIELDS = ['zone_ids', 'facility_ids', 'transport_ids', 'dining_ids'];

const errText = (e) => e.response?.data?.message || e.message || 'Something went wrong';

// Security / gate staff. Creating one also creates their scanner (PWA) login; the venue, zone, facility, transport
// and dining chosen here are their permission set: they show on their Settings page in the scanner and on their
// accreditation card. Saving also creates their accreditation (pending); a super admin sends it for printing from the list.
const SecurityForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [hasAccreditation, setHasAccreditation] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [options, setOptions] = useState(NO_OPTIONS);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState({ rows: [], page: 1, totalPages: 1, total: 0, loading: true });
  const [search, setSearch] = useState('');

  const loadOptions = useCallback(async (venueId) => {
    try {
      const { data } = await axiosInstance.get('/security/options', { params: { venue_id: venueId || undefined } });
      setOptions(data.data);
    } catch (e) {
      notify('error', errText(e));
    }
  }, []);

  const loadList = useCallback(async (page = 1, q = '') => {
    setList((l) => ({ ...l, loading: true }));
    try {
      const { data } = await axiosInstance.get('/security', { params: { page, limit: 10, search: q || undefined } });
      setList({ rows: data.data, page, totalPages: data.totalPages, total: data.totalItems, loading: false });
    } catch (e) {
      setList((l) => ({ ...l, loading: false }));
      notify('error', errText(e));
    }
  }, []);

  useEffect(() => {
    loadOptions(null);
    loadList(1);
  }, [loadOptions, loadList]);

  // the shared searchable dropdown (search box inside the menu) expects this shape
  const userTypeOptions = options.user_types.map((o) => ({ sub_category_id: o.value, sub_category_name_view: o.label, active_status: true }));

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const pick = (opts, ids) => opts.filter((o) => ids.includes(o.value));
  const multi = (field, opts) => ({
    isMulti: true,
    options: opts,
    value: pick(opts, form[field]),
    onChange: (sel) => set({ [field]: (sel || []).map((o) => o.value) }),
  });

  const onVenue = (opt) => {
    // zones, dining and transport belong to a venue, so changing it clears them
    set({ venue_id: opt?.value ?? null, zone_ids: [], dining_ids: [], transport_ids: [] });
    loadOptions(opt?.value);
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0] || null;
    if (file && !file.type.startsWith('image/')) return notify('error', 'Choose an image file');
    if (file && file.size > 5 * 1024 * 1024) return notify('error', 'Photo must be 5 MB or smaller');
    if (photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : '');
  };

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
    setHasAccreditation(false);
    if (photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview('');
    setShowPassword(false);
    loadOptions(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) return notify('error', 'Enter a valid 10-digit Indian mobile number (starts with 6-9)');
    if (!form.venue_id) return notify('error', 'Select a venue');
    if (!form.gender) return notify('error', 'Select a gender');
    if (!form.user_type_id) return notify('error', 'Select the accreditation user type');
    if (!editingId && !photo) return notify('error', 'Upload a photo for the accreditation card');
    if ((!editingId || form.password) && form.password.length < 8) return notify('error', 'Password must be at least 8 characters');
    const body = new FormData();
    ['first_name', 'middle_name', 'last_name', 'email', 'phone', 'gender', 'venue_id', 'user_type_id'].forEach((k) => body.append(k, form[k]));
    if (form.password) body.append('password', form.password);
    ID_FIELDS.forEach((k) => body.append(k, JSON.stringify(form[k])));
    if (photo) body.append('photo', photo);
    setSaving(true);
    try {
      if (editingId) await axiosInstance.put(`/security/${editingId}`, body);
      else await axiosInstance.post('/security', body);
      notify('success', editingId ? 'Security person updated' : 'Security person added. Send their accreditation for printing from the list; they can already sign in to the scanner.');
      reset();
      loadList(1, search);
    } catch (err) {
      notify('error', errText(err));
    } finally {
      setSaving(false);
    }
  };

  const [approveRow, setApproveRow] = useState(null); // the row shown in the send-for-printing popup
  const [approving, setApproving] = useState(false);
  const approve = async () => {
    const r = approveRow;
    setApproving(true);
    try {
      const { data } = await axiosInstance.post(`/user/registrationform/approve/${r.registration_data_id}`);
      if (data?.success === false) throw new Error(data.message || 'Approval failed');
      notify('success', `${r.name} sent for printing. Ready in Accred Printing.`);
      setApproveRow(null);
      loadList(list.page, search);
    } catch (err) {
      notify('error', errText(err));
    } finally {
      setApproving(false);
    }
  };

  const names = (items) => (items.length ? items.map((i) => i.name).join(', ') : '—');

  return (
    <>
      <PageTitle motherMenu="Users" activeMenu="Security Form" />

      <div className="card mb-4">
        <div className="card-header">
          <h4 className="card-title mb-0">{editingId ? 'Edit Security Person' : 'Add Security Person'}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={submit}>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">First name *</label>
                <input className="form-control" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} minLength={2} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Middle name</label>
                <input className="form-control" value={form.middle_name} onChange={(e) => set({ middle_name: e.target.value })} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Last name *</label>
                <input className="form-control" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Email * <small className="text-muted">(scanner login id)</small></label>
                <input type="email" className="form-control" value={form.email} onChange={(e) => set({ email: e.target.value })} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Phone *</label>
                <input className="form-control" inputMode="tel" maxLength={10} pattern="[6-9][0-9]{9}" title="10-digit mobile number starting with 6-9" placeholder="e.g. 9876543210" value={form.phone} onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} required />
              </div>
              <div className="col-md-4">
                <label className="form-label">Gender *</label>
                <Select options={GENDERS} value={GENDERS.find((o) => o.value === form.gender) || null} onChange={(o) => set({ gender: o?.value || '' })} placeholder="Select gender" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Accreditation user type *</label>
                <SearchableUserTypeDropdown
                  userTypes={userTypeOptions}
                  value={form.user_type_id}
                  onChange={(id) => set({ user_type_id: id })}
                  placeholder={options.user_types.length ? 'Select user type' : 'No accreditation user types'}
                  disabled={!!(editingId && hasAccreditation)}
                  showCount={false}
                  useStandardFormControl
                  className="form-select"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Photo {editingId ? '' : '*'} <small className="text-muted">{editingId ? '(choose to replace)' : '(printed on the card)'}</small>
                </label>
                <input type="file" accept="image/*" className="form-control" onChange={onPhoto} />
                {photoPreview && <img src={photoPreview} alt="Preview" className="mt-2 rounded" style={{ width: 72, height: 72, objectFit: 'cover' }} />}
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  {editingId ? 'New password' : 'Password *'} <small className="text-muted">{editingId ? '(leave blank to keep)' : '(min 8 characters)'}</small>
                </label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={form.password}
                    onChange={(e) => set({ password: e.target.value })}
                    autoComplete="new-password"
                    minLength={8}
                    required={!editingId}
                  />
                  <button
                    type="button"
                    className="btn eye-btn"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Venue *</label>
                <Select options={options.venues} value={options.venues.find((o) => o.value === form.venue_id) || null} onChange={onVenue} placeholder="Select venue" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Zone</label>
                <Select {...multi('zone_ids', options.zones)} placeholder={form.venue_id ? 'Select zones' : 'Select a venue first'} isDisabled={!form.venue_id} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Facility</label>
                <Select {...multi('facility_ids', options.facilities)} placeholder="Select facilities" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Transport</label>
                <Select {...multi('transport_ids', options.transport)} placeholder={form.venue_id ? 'Select transport' : 'Select a venue first'} isDisabled={!form.venue_id} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Dining</label>
                <Select {...multi('dining_ids', options.dining)} placeholder={form.venue_id ? 'Select dining' : 'Select a venue first'} isDisabled={!form.venue_id} />
              </div>
            </div>
            <div className="mt-4 d-flex gap-2">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
              </button>
              {editingId && (
                <button className="btn btn-outline-secondary" type="button" onClick={reset}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="card-title mb-0">Security Staff ({list.total})</h4>
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 260 }}
            placeholder="Search name, email or phone"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              loadList(1, e.target.value);
            }}
          />
        </div>
        <div className="card-body table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Phone</th><th>Gender</th><th>Venue</th><th>Zone</th><th>Facility</th><th>Transport</th><th>Dining</th><th>Accreditation</th><th>Status</th><th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.phone}</td>
                  <td>{GENDERS.find((g) => g.value === r.gender)?.label || '—'}</td>
                  <td>{r.venue}</td>
                  <td>{names(r.zones)}</td>
                  <td>{names(r.facilities)}</td>
                  <td>{names(r.transport)}</td>
                  <td>{names(r.dining)}</td>
                  <td>
                    <span className={`badge ${r.accreditation === 'Sent For Printing' ? 'bg-success' : r.accreditation === 'Blocked' || r.accreditation === 'Revoked' || r.accreditation === 'Disapproved' ? 'bg-danger' : 'bg-warning text-dark'}`}>{r.accreditation}</span>
                    {r.user_type && <div className="small text-muted">{r.user_type}{r.registration_data_id ? ` · #${r.registration_data_id}` : ''}</div>}
                  </td>
                  <td><span className={`badge ${r.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>{r.status}</span></td>
                  <td className="text-nowrap text-center">
                    {r.accreditation === 'Pending approval' && r.registration_data_id ? (
                      isApprover() ? (
                        <button className="btn btn-success btn-sm" onClick={() => setApproveRow(r)}>
                          Send For Printing
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" disabled title="A super admin has to approve this accreditation">
                          Awaiting approval
                        </button>
                      )
                    ) : r.accreditation === 'Sent For Printing' ? (
                      <button className="btn btn-secondary btn-sm" disabled>Sent For Printing</button>
                    ) : ['Blocked', 'Revoked', 'Disapproved'].includes(r.accreditation) ? (
                      <button className="btn btn-danger btn-sm" disabled>{r.accreditation}</button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {!list.loading && !list.rows.length && (
                <tr><td colSpan={12} className="text-center text-muted">No security staff added yet.</td></tr>
              )}
            </tbody>
          </table>
          {list.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center">
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page <= 1} onClick={() => loadList(list.page - 1, search)}>Previous</button>
              <span className="small text-muted">Page {list.page} of {list.totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page >= list.totalPages} onClick={() => loadList(list.page + 1, search)}>Next</button>
            </div>
          )}
        </div>
      </div>

      <Modal show={!!approveRow} onHide={() => !approving && setApproveRow(null)} centered>
        <Modal.Header closeButton={!approving}>
          <Modal.Title className="fs-5">Send for printing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approveRow && (
            <>
              <p className="mb-3">
                Send the accreditation of <strong>{approveRow.name}</strong> for printing? It will move to <strong>Sent For Printing</strong> and
                appear in Accred Printing.
              </p>
              <ul className="list-unstyled small text-muted mb-0">
                <li>Email: {approveRow.email}</li>
                <li>User type: {approveRow.user_type || '—'}</li>
                <li>Venue: {approveRow.venue}</li>
                <li>Accreditation #{approveRow.registration_data_id}</li>
              </ul>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setApproveRow(null)} disabled={approving}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={approve} disabled={approving}>
            {approving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Sending…
              </>
            ) : (
              'Send For Printing'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SecurityForm;
